<template>
	<Select v-model="innerValue" :name :options :label :state multiple>
		<template #error>
			<Errors :errors="field.errors.value" />
		</template>
		<slot />
	</Select>
</template>

<script lang="ts">
import type { Options as Select2Options } from 'select2'
import { useField } from 'vee-validate'
import { defineComponent, ref, computed } from 'vue'
import Errors from './Errors.vue'
import Select from './Select.vue'

export default defineComponent({
	name: 'MultiSelectField',
	components: { Errors, Select },
	emits: ['update:modelValue'],

	props: {
		modelValue: { type: Array as () => string[], default: () => [] },
		options:    { type: Object as () => Select2Options },
		name:       { type: String, required: true },
		rules:      { type: [String, Object] },
		label:      { type: String, default: '' },
	},

	setup(props, { emit }) {
		// field is the vee-validate field — initialised once, never re-synced from prop
		const { value: fieldValue, errors, meta, setValue } = useField<string[]>(
			props.name,
			props.rules as string,
			{
				initialValue: Array.isArray(props.modelValue) ? [...props.modelValue] : [],
				label: props.label,
			}
		)

		// innerValue is what <Select v-model> binds to.
		// It is seeded once; after that it only changes when the user picks something.
		const innerValue = ref<string[]>(
			Array.isArray(props.modelValue) ? [...props.modelValue] : []
		)

		// Intercept writes that come FROM Select2 via v-model.
		// This computed replaces v-model on innerValue so we can side-effect cleanly.
		const innerValueProxy = computed<string[]>({
			get() {
				return innerValue.value
			},
			set(val: string[]) {
				const next = Array.isArray(val) ? [...val] : []
				// Guard: only propagate if something actually changed
				if (JSON.stringify(next) === JSON.stringify(innerValue.value)) return
				innerValue.value = next   // update local ref
				setValue(next)            // update vee-validate
				emit('update:modelValue', next)  // update parent — does NOT feed back in
			},
		})

		const state = computed(() => {
			if (!meta.dirty) return ''
			return meta.valid ? 'is-valid' : 'is-invalid'
		})

		return { innerValue: innerValueProxy, field: { errors, meta }, state }
	},
})
</script>